import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { DemandPricingMode } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';

const MODES: DemandPricingMode[] = ['OFF', 'SUGGEST', 'AUTO'];

function modeKey(mode: DemandPricingMode): 'off' | 'suggest' | 'auto' {
  if (mode === 'SUGGEST') {
    return 'suggest';
  }
  if (mode === 'AUTO') {
    return 'auto';
  }
  return 'off';
}

export function DemandPricingCard(): ReactElement {
  const { api } = useAuth();
  const [mode, setMode] = useState<DemandPricingMode>('OFF');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getModelShowPricing()
      .then((result) => {
        if (!cancelled) {
          setMode(result.pricing.demandPricingMode ?? 'OFF');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('common.studio.settings.demandLoadError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  async function handleChange(next: DemandPricingMode): Promise<void> {
    const previous = mode;
    setMode(next);
    setIsSaving(true);
    setError(null);
    try {
      const result = await api.updateModelShowPricing({ demandPricingMode: next });
      setMode(result.pricing.demandPricingMode);
    } catch (err) {
      setMode(previous);
      setError(err instanceof ApiError ? err.message : t('common.studio.settings.demandSaveError'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1.125rem' }}>{t('common.studio.settings.demandTitle')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.settings.demandHint')}
      </p>
      {isLoading ? (
        <p style={{ margin: 0, color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.settings.demandLoading')}
        </p>
      ) : (
        <fieldset style={{ border: 'none', margin: 0, padding: 0 }} aria-label={t('common.studio.settings.demandTitle')}>
          {MODES.map((value) => (
            <label key={value} style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.55rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="demand-pricing-mode"
                value={value}
                checked={mode === value}
                disabled={isSaving}
                onChange={() => {
                  void handleChange(value);
                }}
              />
              <span>
                <strong>{t(`common.studio.settings.demandMode.${modeKey(value)}`)}</strong>
                <span style={{ display: 'block', color: 'var(--fm-color-text-muted)', fontSize: '0.8125rem' }}>
                  {t(`common.studio.settings.demandModeHint.${modeKey(value)}`)}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      {error ? <p className="fm-error" style={{ margin: '0.75rem 0 0' }}>{error}</p> : null}
      <p style={{ margin: '0.75rem 0 0', color: 'var(--fm-color-text-muted)', fontSize: '0.8125rem' }}>
        {t('common.studio.settings.demandEngineNote')}
      </p>
    </div>
  );
}
